import { Flex } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getContragentsSearch } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";
import { arrayToRecord } from "tm-shared/utils";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncOptions } from "@/hooks/useAsyncOptions";
import { FieldName } from "@/pages/SV/MarketCreate/MarketCreate.constants";
import {
    setMarketCreatePageContragentsMap,
    setMarketCreatePageModal,
    setMarketCreatePageOptionsCache,
} from "@/store/slices/ui";
import { NewMarketFormValues } from "@/types/form";
import { MarketCreateModal } from "@/types/modals";

interface LegalEntitySectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const LegalEntitySection = ({
    activeMap,
    resetBelow,
}: LegalEntitySectionProps) => {
    const dispatch = useAppDispatch();

    const { optionCache, contragentsMap } = useAppSelector(
        (s) => s.ui.marketCreatePage,
    );

    const [searchQuery, setSearchQuery] = useState("");

    const { control, setValue } = useFormContext<NewMarketFormValues>();

    const region = useWatch<NewMarketFormValues, "region">({
        name: "region",
        control,
    });
    const legalEntity = useWatch<NewMarketFormValues>({
        name: "legalEntity",
        control,
    });

    const handleAddLegalEntity = useCallback(() => {
        dispatch(
            setMarketCreatePageModal({
                type: MarketCreateModal.AddLegalEntity,
            }),
        );
    }, [dispatch]);

    const {
        options: fetchedOptions,
        loading: loadingLegalEntity,
        data: allLegalEntities,
    } = useAsyncOptions({
        fetchFn: (signal) =>
            getContragentsSearch(
                {
                    query: searchQuery,
                    mainRegionId: region,
                },
                {
                    axiosOverrides: { signal },
                },
            ),
        selectData: (response) => Object.values(response.result),
        mapToOption: (c) => {
            const alias = c.alias ? ` (${c.alias})` : "";
            const label = c.fullName + alias;

            return { label, value: c.id };
        },
        dependencies: [searchQuery, region],
        enabled:
            ((!searchQuery && !legalEntity) || searchQuery.length >= 3) &&
            activeMap.legalEntity,
        debounceMs: 500,
    });

    useEffect(() => {
        if (fetchedOptions?.length) {
            dispatch(
                setMarketCreatePageOptionsCache({
                    fieldName: "legalEntity",
                    options: fetchedOptions,
                }),
            );

            if (allLegalEntities?.result) {
                const contragentsMap = arrayToRecord(
                    allLegalEntities.result,
                    "id",
                );

                dispatch(setMarketCreatePageContragentsMap(contragentsMap));
            }
        }
    }, [allLegalEntities?.result, dispatch, fetchedOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("legalEntity", "");
            resetBelow("legalEntity");
        },
        [resetBelow, setValue],
    );

    const handleAfterChange = useCallback(() => {
        resetBelow("legalEntity");
    }, [resetBelow]);

    const currentContragent = contragentsMap[legalEntity ?? ""];

    return (
        <Flex vertical gap={8}>
            <RHFAutocomplete
                name="legalEntity"
                options={optionCache?.["legalEntity"] ?? []}
                label="Юридична особа"
                rules={{
                    required: validationMessage.required,
                }}
                actionOption={{
                    label: "Додати юридичну особу",
                    onClick: handleAddLegalEntity,
                }}
                loading={loadingLegalEntity}
                allowClear={false}
                disabled={!activeMap.legalEntity}
                onAfterChange={handleAfterChange}
                onInputChange={handleInputChange}
                selectProps={{ size: "large" }}
            />

            {currentContragent && currentContragent.id.length <= 12 && (
                <Flex
                    vertical
                    gap={4}
                    style={{
                        opacity: activeMap.legalEntity ? 1 : 0.4,
                        pointerEvents: "none",
                    }}
                >
                    <Flex gap={4}>
                        <PrimaryText fontSize={12}>ІНН/ЄДРПОУ:</PrimaryText>
                        <PrimaryText>{currentContragent.id}</PrimaryText>
                    </Flex>

                    {currentContragent.ceoName && (
                        <Flex gap={4}>
                            <PrimaryText fontSize={12}>Директор:</PrimaryText>
                            <PrimaryText>
                                {currentContragent.ceoName}
                            </PrimaryText>
                        </Flex>
                    )}
                </Flex>
            )}
        </Flex>
    );
};
